import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { useAuth } from '../AuthContext';
import { ConfirmModal, NoticeModal } from '../Dialogs';
import { FaArrowLeft, FaExclamationTriangle, FaChevronUp, FaChevronDown, FaCopy, FaPaste, FaTimes, FaCheckCircle, FaTrash, FaFileExcel, FaSpinner, FaEdit } from 'react-icons/fa';

// --- FieldSelectorModal Component ---
const FieldSelectorModal = ({ isOpen, onClose, interfaceName, fields, selectedFieldIds, onSelectionChange, onFieldUpdated, onFieldDeleted }) => {
    const modalBodyRef = useRef(null);
    const [formMode, setFormMode] = useState(null);
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [error, setError] = useState('');
    const [fieldData, setFieldData] = useState({});
    const [deletingField, setDeletingField] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
    }, [isOpen]);

    const allFieldsFlat = useMemo(() => {
        return Object.values(fields).flat();
    }, [fields]);

    if (!isOpen) return null;

    const hasMultipleFiles = Object.keys(fields).length > 1;

    const handleCheckboxChange = (field, isChecked) => {
        const isLocked = field.is_mandatory && !hasMultipleFiles;
        if (isLocked) return;

        const newSelectedIds = isChecked
            ? [...selectedFieldIds, field.id]
            : selectedFieldIds.filter(id => id !== field.id);
        onSelectionChange(interfaceName, newSelectedIds);
    };

    const handleSelectAll = () => {
        const allFieldIds = allFieldsFlat.map(f => f.id);
        onSelectionChange(interfaceName, allFieldIds);
    };

    const handleDeselectAll = () => {
        const mandatoryFieldsToKeep = allFieldsFlat.filter(f => f.is_mandatory);
        const mandatoryIdsToKeep = mandatoryFieldsToKeep.map(f => f.id);
        onSelectionChange(interfaceName, mandatoryIdsToKeep);
    };

    // --- Custom Field Edit/Delete Handlers ---
    const openEditFieldForm = (fieldToEdit) => {
        setFormMode('edit');
        setEditingFieldId(fieldToEdit.id);
        setFieldData({
            db_field_name: fieldToEdit.db_field_name ?? '',
            file_header_name: fieldToEdit.file_header_name ?? '',
            data_type: fieldToEdit.data_type ?? 'STRING',
            description: fieldToEdit.description ?? '',
            is_mandatory: fieldToEdit.is_mandatory ?? false,
            is_custom: fieldToEdit.is_custom ?? true,
            table_in_relex: fieldToEdit.table_in_relex ?? '',
            ui_field_in_relex: fieldToEdit.ui_field_in_relex ?? '',
            used_for: fieldToEdit.used_for ?? '',
            example_value: fieldToEdit.example_value ?? '',
            source_file: fieldToEdit.source_file ?? ''
        });
        if (modalBodyRef.current) {
            modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'combined_field_name') {
            setFieldData(prev => ({ ...prev, db_field_name: value, file_header_name: value }));
        } else {
            setFieldData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!fieldData.db_field_name.trim()) {
            setError('Field Name is required.');
            return;
        }
        
        try {
            const response = await axiosInstance.put(`/api/master-interface-fields/${editingFieldId}`, fieldData);
            onFieldUpdated(response.data, interfaceName);
            setFormMode(null);
        } catch (err) {
            if (err.response?.status === 401) return;
            setError(err.response?.data?.detail || err.message);
        }
    };

    const confirmDeleteCustomField = async () => {
        if (!deletingField) return;
        const fieldId = deletingField.id;
        setDeletingField(null);
        try {
            await axiosInstance.delete(`/api/master-interface-fields/${fieldId}`);
            onFieldDeleted(fieldId, interfaceName);
            if (editingFieldId === fieldId) {
                setFormMode(null);
            }
        } catch (err) {
            setDeleteError("Failed to delete custom field. It may be in use by another project.");
        }
    };

    const formattedInterfaceName = (interfaceName || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

    return (
        <>
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Fields for {formattedInterfaceName}</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body" ref={modalBodyRef}>
                    <div className="dropdown-actions">
                        <span>{selectedFieldIds.length} / {allFieldsFlat.length} selected</span>
                        <div>
                            <button type="button" onClick={handleSelectAll}>Select All</button>
                            <button type="button" onClick={handleDeselectAll}>Deselect Optional</button>
                        </div>
                    </div>

                    <div className="field-modal-disclaimer">
                        Note: Custom and regular fields can be added, edited and imported after the project is created, via the Edit Project page.<br />
                        Mandatory fields may also be overriden.
                    </div>

                    {formMode === 'edit' && (
                        <div className="custom-field-form" style={{ margin: '1rem 1.5rem' }}>
                            <div className="form-header-row">
                                <h4>Edit Custom Field</h4>
                                <button type="button" onClick={() => setFormMode(null)} className="modal-close-button"><FaTimes /></button>
                            </div>
                            {error && <div className="form-feedback error" style={{ marginBottom: '1rem' }}>{error}</div>}
                            <div className="form-grid">
                                <div className="input-group">
                                    <label className="input-label">Field Name (Header / DB Name) *</label>
                                    <input type="text" name="combined_field_name" value={fieldData.db_field_name} onChange={handleInputChange} placeholder="e.g. custom_sales_region" required />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Data Type</label>
                                    <select name="data_type" value={fieldData.data_type} onChange={handleInputChange}>
                                        <option value="STRING">STRING</option>
                                        <option value="DATE">DATE</option>
                                        <option value="BOOLEAN">BOOLEAN</option>
                                        <option value="DOUBLE">DOUBLE</option>
                                        <option value="INTEGER">INTEGER</option>
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Table in RELEX</label>
                                    <input type="text" name="table_in_relex" value={fieldData.table_in_relex} onChange={handleInputChange} />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">UI Field in RELEX</label>
                                    <input type="text" name="ui_field_in_relex" value={fieldData.ui_field_in_relex} onChange={handleInputChange} />
                                </div>
                                <div className="input-group full-width">
                                    <label className="input-label">Description</label>
                                    <textarea name="description" value={fieldData.description} onChange={handleInputChange} rows="2" />
                                </div>
                                <div className="input-group full-width">
                                    <label className="input-label">Used For</label>
                                    <input type="text" name="used_for" value={fieldData.used_for} onChange={handleInputChange} />
                                </div>
                                <div className="input-group full-width">
                                    <label className="input-label">Example Value</label>
                                    <input type="text" name="example_value" value={fieldData.example_value} onChange={handleInputChange} />
                                </div>
                                <div className="input-group full-width">
                                    <Checkbox id="is_mandatory_custom" name="is_mandatory" checked={fieldData.is_mandatory} onChange={handleInputChange} label="Is this field mandatory?" />
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setFormMode(null)} className="action-button secondary">Cancel</button>
                                <button type="button" onClick={handleFormSubmit} className="action-button primary">Save Changes</button>
                            </div>
                        </div>
                    )}

                    <div className="field-groups-container">
                        {Object.entries(fields).sort().map(([fileName, fileFields]) => (
                            <div key={fileName} className="file-group">
                                <h4 className="file-group-header">{fileName}</h4>
                                <ul className="field-list">
                                    {fileFields.map(field => {
                                        const isSelected = selectedFieldIds.includes(field.id);
                                        const isLocked = field.is_mandatory && !hasMultipleFiles;

                                        return (
                                            <li key={field.id} className={`field-item ${field.is_mandatory ? 'is-mandatory' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    id={`field-modal-${field.id}`}
                                                    checked={isSelected}
                                                    onChange={(e) => handleCheckboxChange(field, e.target.checked)}
                                                    disabled={isLocked}
                                                />
                                                <div className="field-item-content">
                                                    <label htmlFor={`field-modal-${field.id}`} className="field-item-label-container">
                                                        <span className="field-label-text">
                                                            {field.file_header_name || field.db_field_name}
                                                        </span>
                                                        <div className="field-tags-and-actions">
                                                            {field.is_custom && <span className="custom-tag">Custom</span>}
                                                            {field.is_mandatory && <span className="mandatory-tag">Mandatory</span>}
                                                            {field.is_custom && (
                                                                <div className="custom-field-actions">
                                                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditFieldForm(field); }} className="edit-btn" title="Edit Custom Field"><FaEdit /></button>
                                                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeletingField(field); }} className="delete-btn" title="Delete Custom Field"><FaTrash /></button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </label>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" onClick={onClose} className="action-button primary">Done</button>
                </div>
            </div>
        </div>

            <ConfirmModal
                isOpen={!!deletingField}
                tone="danger"
                title="Delete Custom Field?"
                message={<>
                    <strong style={{ color: 'var(--text-color)' }}>{deletingField?.file_header_name || deletingField?.db_field_name}</strong> will be permanently deleted from the master field list.
                    <br /><br />
                    This cannot be undone.
                </>}
                confirmLabel="Delete Field"
                cancelLabel="Keep Field"
                onConfirm={confirmDeleteCustomField}
                onClose={() => setDeletingField(null)}
            />

            <NoticeModal
                isOpen={!!deleteError}
                tone="error"
                title="Could Not Delete Field"
                message={deleteError}
                onClose={() => setDeleteError('')}
            />
        </>
    );
};

// --- DYNAMIC METADATA MAP ---
const INTERFACE_METADATA = {
    // Drop 1 - Master Data
    "merchandising_product_groups": { category: "Drop 1 - Master Data", label: "Merchandising product groups", modules: ["S&A"] },
    "locations": { category: "Drop 1 - Master Data", label: "Locations", modules: ["F&R", "S&A", "P&P"] },
    "product_groups": { category: "Drop 1 - Master Data", label: "Product groups", modules: ["F&R", "S&A", "P&P"] },
    "product_locations": { category: "Drop 1 - Master Data", label: "Product-locations", modules: ["F&R", "S&A"] },
    "products": { category: "Drop 1 - Master Data", label: "Products", modules: ["F&R", "S&A", "P&P"] },
    "promotion_assortments": { category: "Drop 1 - Master Data", label: "Promotion assortments", modules: ["P&P"] },
    "campaigns": { category: "Drop 1 - Master Data", label: "Campaigns", modules: ["F&R", "P&P"] },
    "price_calendars": { category: "Drop 1 - Master Data", label: "Price calendars", modules: ["F&R"] },
    "product_location_price_calendars": { category: "Drop 1 - Master Data", label: "Product-location price calendars", modules: ["F&R"] },
    "sales_prices": { category: "Drop 1 - Master Data", label: "Sales prices", modules: ["P&P"] },
    "purchase_prices": { category: "Drop 1 - Master Data", label: "Purchase prices", modules: ["P&P"] },
    "competitor_prices": { category: "Drop 1 - Master Data", label: "Competitor prices", modules: ["P&P"] },
    "product_location_campaigns": { category: "Drop 1 - Master Data", label: "Product-location-campaigns", modules: ["F&R", "P&P"] },
    "location_hierarchy": { category: "Drop 1 - Master Data", label: "Location hierarchy", modules: ["F&R", "S&A"] },
    "opening_times": { category: "Drop 1 - Master Data", label: "Opening times", modules: ["F&R"] },
    "closed_dates": { category: "Drop 1 - Master Data", label: "Closed dates", modules: ["F&R"] },
    "assortments": { category: "Drop 1 - Master Data", label: "Assortments", modules: ["S&A"] },
    "product_location_category_assortment": { category: "Drop 1 - Master Data", label: "Product-location-category-assortment", modules: ["S&A"] },
    "end_customers": { category: "Drop 1 - Master Data", label: "End customers", modules: ["F&R"] },
    "product_end_customers": { category: "Drop 1 - Master Data", label: "Product-end customers", modules: ["F&R"] },
    "product_images": { category: "Drop 1 - Master Data", label: "Product Images", modules: ["S&A"] },
    "floorplan_and_planogram_migrations": { category: "Drop 1 - Master Data", label: "Floorplan and planogram migrations", modules: ["S&A"] },
    "delivery_schedules": { category: "Drop 1 - Master Data", label: "Delivery schedules", modules: ["F&R"] },
    "product_batch_sizes": { category: "Drop 1 - Master Data", label: "Product-batch sizes", modules: ["F&R"] },
    "product_location_batch_sizes": { category: "Drop 1 - Master Data", label: "Product-location-batch sizes", modules: ["F&R"] },
    "product_location_suppliers": { category: "Drop 1 - Master Data", label: "Product-location-suppliers", modules: ["F&R", "P&P"] },
    "suppliers": { category: "Drop 1 - Master Data", label: "Suppliers", modules: ["F&R", "P&P"] },
    "commercial_suppliers": { category: "Drop 1 - Master Data", label: "Commercial suppliers", modules: ["P&P"] },
    "product_location_commercial_suppliers": { category: "Drop 1 - Master Data", label: "Product-location-commercial-suppliers", modules: ["P&P"] },
    "supplier_fundings": { category: "Drop 1 - Master Data", label: "Supplier fundings", modules: ["P&P"] },
    "merchandising_product_catalogues": { category: "Drop 1 - Master Data", label: "Merchandising product catalogues", modules: ["S&A"] },
    "floor_plans": { category: "Drop 1 - Master Data", label: "Floor-plans", modules: ["S&A"] },
    "location_category_spaces": { category: "Drop 1 - Master Data", label: "Location-category-spaces", modules: ["S&A"] },
    "planogram_positions": { category: "Drop 1 - Master Data", label: "Planogram-positions", modules: ["F&R", "S&A"] },
    "product_location_supplier_replenishment_agreements": { category: "Drop 1 - Master Data", label: "Product-location-supplier-replenishment agreements", modules: ["F&R"] },
    "replenishment_agreements": { category: "Drop 1 - Master Data", label: "Replenishment agreements", modules: ["F&R"] },
    "product_location_coordination_groups": { category: "Drop 1 - Master Data", label: "Product-location coordination groups", modules: ["F&R"] },
    "coordination_groups": { category: "Drop 1 - Master Data", label: "Coordination groups", modules: ["F&R"] },
    "constraint_groups": { category: "Drop 1 - Master Data", label: "Constraint groups", modules: ["F&R"] },
    "supplier_locations": { category: "Drop 1 - Master Data", label: "Supplier-locations", modules: ["F&R"] },
    "brackets": { category: "Drop 1 - Master Data", label: "Brackets", modules: ["F&R"] },
    "supplier_location_brackets": { category: "Drop 1 - Master Data", label: "Supplier-location brackets", modules: ["F&R"] },
    "coordination_group_brackets": { category: "Drop 1 - Master Data", label: "Coordination group brackets", modules: ["F&R"] },
    "product_replacements_and_refs": { category: "Drop 1 - Master Data", label: "Product replacements and refs", modules: ["F&R"] },
    "prepack_variants": { category: "Drop 1 - Master Data", label: "Prepack variants", modules: ["F&R"] },
    "supplier_closures": { category: "Drop 1 - Master Data", label: "Supplier closures", modules: ["F&R"] },
    "category_clusters": { category: "Drop 1 - Master Data", label: "Category-clusters", modules: ["S&A"] },
    "bill_of_materials": { category: "Drop 1 - Master Data", label: "Bill-of-materials", modules: ["F&R"] },
    "barcodes": { category: "Drop 1 - Master Data", label: "Barcodes", modules: [] },

    // Drop 2 - Historical Data
    "historical_balances": { category: "Drop 2 - Historical Data", label: "Historical balances", modules: ["F&R", "S&A", "P&P"] },
    "historical_sales": { category: "Drop 2 - Historical Data", label: "Historical sales", modules: ["F&R", "S&A", "P&P"] },
    "historical_deliveries": { category: "Drop 2 - Historical Data", label: "Historical deliveries", modules: ["F&R", "S&A"] },
    "historical_spoilages": { category: "Drop 2 - Historical Data", label: "Historical spoilages", modules: ["F&R", "S&A"] },
    "historical_transfers": { category: "Drop 2 - Historical Data", label: "Historical transfers", modules: ["F&R"] },
    "historical_inventory_checks": { category: "Drop 2 - Historical Data", label: "Historical inventory checks", modules: ["F&R"] },
    "historical_adjustments": { category: "Drop 2 - Historical Data", label: "Historical adjustments", modules: ["F&R"] },
    "historical_lost_sales": { category: "Drop 2 - Historical Data", label: "Historical lost sales", modules: ["F&R"] },
    "historical_campaigns": { category: "Drop 2 - Historical Data", label: "Historical campaigns", modules: ["F&R", "P&P"] },
    "historical_product_location_campaigns": { category: "Drop 2 - Historical Data", label: "Historical product-location-campaigns", modules: ["F&R", "P&P"] },
    "historical_price_calendars": { category: "Drop 2 - Historical Data", label: "Historical Price calendars", modules: ["F&R"] },
    "historical_sales_prices": { category: "Drop 2 - Historical Data", label: "Historical Sales prices", modules: ["P&P"] },
    "historical_purchase_prices": { category: "Drop 2 - Historical Data", label: "Historical Purchase prices", modules: ["P&P"] },
    "historical_supplier_fundings": { category: "Drop 2 - Historical Data", label: "Historical Supplier fundings", modules: ["P&P"] },
    "historical_promotion_assortments": { category: "Drop 2 - Historical Data", label: "Historical promotion assortments", modules: ["P&P"] },
    "historical_footfalls": { category: "Drop 2 - Historical Data", label: "Historical footfalls", modules: [] },

    // Drop 3 - Transactional Data
    "sales": { category: "Drop 3 - Transactional Data", label: "Sales", modules: ["F&R", "S&A", "P&P"] },
    "end_customer_transactions": { category: "Drop 3 - Transactional Data", label: "End customer transactions", modules: ["F&R"] },
    "balances": { category: "Drop 3 - Transactional Data", label: "Balances", modules: ["F&R", "S&A", "P&P"] },
    "batch_balances": { category: "Drop 3 - Transactional Data", label: "Batch balances", modules: ["F&R"] },
    "open_purchase_orders": { category: "Drop 3 - Transactional Data", label: "Open purchase orders", modules: ["F&R"] },
    "open_sales_orders": { category: "Drop 3 - Transactional Data", label: "Open sales orders", modules: ["F&R"] },
    "deliveries": { category: "Drop 3 - Transactional Data", label: "Deliveries", modules: ["F&R", "S&A"] },
    "spoilages": { category: "Drop 3 - Transactional Data", label: "Spoilages", modules: ["F&R", "S&A"] },
    "transfers": { category: "Drop 3 - Transactional Data", label: "Transfers", modules: ["F&R"] },
    "inventory_checks": { category: "Drop 3 - Transactional Data", label: "Inventory checks", modules: ["F&R"] },
    "adjustments": { category: "Drop 3 - Transactional Data", label: "Adjustments", modules: ["F&R"] },
    "lost_sales": { category: "Drop 3 - Transactional Data", label: "Lost sales", modules: ["F&R"] },
    "footfalls": { category: "Drop 3 - Transactional Data", label: "Footfalls", modules: [] },
    "dc_transactions": { category: "Drop 3 - Transactional Data", label: "DC transactions", modules: ["F&R"] },
    "orders": { category: "Drop 3 - Transactional Data", label: "Orders", modules: ["F&R"] },

    // Drop 4 - Output Interfaces
    "output_order_proposals": { category: "Drop 4 - Outbound", label: "Output: Order Proposals", modules: ["F&R"] },
    "output_reserve_order_proposals": { category: "Drop 4 - Outbound", label: "Output: Reserve Order Proposals", modules: ["F&R"] },
    "output_sales_forecasts": { category: "Drop 4 - Outbound", label: "Output: Sales Forecasts", modules: ["F&R", "S&A"] },
    "output_clearance": { category: "Drop 4 - Outbound", label: "Output: Clearance", modules: ["F&R"] },
    "output_workload_driver_forecast_day_level": { category: "Drop 4 - Outbound", label: "Output: Workload driver forecast - day level", modules: [] },
    "output_workload_driver_forecast_15_min_level": { category: "Drop 4 - Outbound", label: "Output: Workload driver forecast - 15 min level", modules: [] },
    "output_shelf_edge_labels": { category: "Drop 4 - Outbound", label: "Output: Shelf Edge Labels", modules: ["S&A"] },
    "output_inventory_exceptions": { category: "Drop 4 - Outbound", label: "Output: Inventory exceptions", modules: ["F&R"] },
    "output_promotions": { category: "Drop 4 - Outbound", label: "Output: Promotions", modules: ["P&P"] },
    "output_prices": { category: "Drop 4 - Outbound", label: "Output: Prices", modules: ["P&P"] },
    "output_assortments": { category: "Drop 4 - Outbound", label: "Output: Assortments", modules: ["S&A"] },
};

const DEFAULT_TASKS = ["Sample Delivery", "Technical Validation", "Full Extraction", "Connect Integration", "Upload in RELEX", "Functional Validation"];
const DROP_NAMES = ["Drop 1 - Master Data", "Drop 2 - Historical Data", "Drop 3 - Transactional Data", "Drop 4 - Outbound", "Drop 5 - Additional Data"];
// Manual drop limit. An imported Interface Document that schedules more drops raises it to match.
const MAX_DROPS = 5;

// A drop is named "Drop <n> - <label>". The "Drop <n>" prefix is fixed; only the label is editable.
const DROP_NAME_PATTERN = /^(Drop\s*\d+)\s*(?:-\s*(.*))?$/i;
const getDropPrefix = (name = '') => {
    const match = (name || '').match(DROP_NAME_PATTERN);
    return match ? match[1].replace(/\s+/g, ' ') : null;
};
const getDropLabel = (name = '') => {
    const match = (name || '').match(DROP_NAME_PATTERN);
    return match ? (match[2] || '') : (name || '');
};
const buildDropName = (prefix, label) => {
    if (!prefix) return label;
    return label.trim() ? `${prefix} - ${label}` : prefix;
};
// Drops past the defaults reuse the "Additional Data" label; it stays editable like any other.
const getStandardDropName = (dropNumber) => DROP_NAMES[dropNumber - 1] || `Drop ${dropNumber} - Additional Data`;
// The number in a drop's "Drop <n>" prefix, i.e. what the user sees. Saving renumbers drops by
// position, so drop_number can disagree with the name; it is only the fallback for unprefixed names.
const getDropNumber = (drop) => {
    const prefix = getDropPrefix(drop.name);
    return prefix ? parseInt(prefix.replace(/\D/g, ''), 10) : drop.drop_number;
};
// Drops are matched to interface categories by their "Drop <n>" prefix so a renamed
// drop is still recognised as the same slot as its default category name.
const isSameDropSlot = (nameA = '', nameB = '') => {
    const prefixA = getDropPrefix(nameA);
    const prefixB = getDropPrefix(nameB);
    if (prefixA && prefixB) return prefixA.toLowerCase() === prefixB.toLowerCase();
    return nameA === nameB;
};

// --- MultiUserSelect Component ---
const MultiUserSelect = ({ allUsers, selectedUserIds, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);

    const selectedUsers = useMemo(() =>
        selectedUserIds.map(id => allUsers.find(u => u.id === id)).filter(Boolean),
        [selectedUserIds, allUsers]
    );

    const availableUsers = useMemo(() =>
        allUsers.filter(u => !selectedUserIds.includes(u.id)),
        [selectedUserIds, allUsers]
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleRemoveUser = (userIdToRemove) => {
        onChange(selectedUserIds.filter(id => id !== userIdToRemove));
    };

    const handleAddUser = (userIdToAdd) => {
        onChange([...selectedUserIds, userIdToAdd]);
        setIsOpen(false);
    };

    return (
        <div className="multi-select-container" ref={selectRef}>
            <div className="selected-users-pills" onClick={() => setIsOpen(!isOpen)}>
                {selectedUsers.length > 0 ? selectedUsers.map(user => (
                    <span key={user.id} className="user-pill">
                        {user.username}
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveUser(user.id); }}>&times;</button>
                    </span>
                )) : <span className="no-users-text">Unassigned</span>}
            </div>
            {isOpen && (
                <div className="user-dropdown-list">
                    {availableUsers.length > 0 ? availableUsers.map(user => (
                        <div key={user.id} className="user-dropdown-item" onClick={() => handleAddUser(user.id)}>
                            {user.username}
                        </div>
                    )) : <div className="user-dropdown-item disabled">No more users to add</div>}
                </div>
            )}
        </div>
    );
};

// --- Checkbox Component ---
const Checkbox = ({ id, name, checked, onChange, label, children, disabled }) => {
    return (
        <div className="checkbox-container">
            <div className="checkbox-wrapper">
                <input
                    id={id}
                    type="checkbox"
                    name={name}
                    checked={checked}
                    onChange={onChange}
                    className="checkbox-input"
                    disabled={disabled}
                />
                <label htmlFor={id} className={`checkbox-label ${disabled ? 'disabled' : ''}`}>{label}</label>
            </div>
            {children}
        </div>
    );
};

// --- CreateProject Component (Main Logic) ---
const CreateProject = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [categorizedInterfaces, setCategorizedInterfaces] = useState({});
    const [flatAllInterfaces, setFlatAllInterfaces] = useState([]);

    const loadDraft = () => {
        try {
            const saved = sessionStorage.getItem('wysupp_create_project_draft');
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error("Failed to load draft", e);
        }
        return null;
    };
    const draft = loadDraft();

    const [projectName, setProjectName] = useState(draft?.projectName || '');
    const [activeInterfaces, setActiveInterfaces] = useState(draft?.activeInterfaces || {});
    const [drops, setDrops] = useState(draft?.drops || []);
    const [dropLimit, setDropLimit] = useState(draft?.dropLimit || MAX_DROPS);
    const [assignedUsers, setAssignedUsers] = useState(draft?.assignedUsers || []);
    const [selectedFields, setSelectedFields] = useState(draft?.selectedFields || {});
    const [activeCategory, setActiveCategory] = useState(draft?.activeCategory || "");
    const [activeDropId, setActiveDropId] = useState(draft?.activeDropId || null);
    const [expandedInterfaces, setExpandedInterfaces] = useState(draft?.expandedInterfaces || {});
    
    // --- Tracking newly imported database custom fields ---
    const [isParsingExcel, setIsParsingExcel] = useState(false);
    const [importedCustomFieldIds, setImportedCustomFieldIds] = useState(draft?.importedCustomFieldIds || []);
    const [importedCustomInterfaceIds, setImportedCustomInterfaceIds] = useState(draft?.importedCustomInterfaceIds || []);
    const excelInputRef = useRef(null);

    const [incompleteFieldsWarning, setIncompleteFieldsWarning] = useState(0);
    const [emptyInterfacesWarning, setEmptyInterfacesWarning] = useState(0);

    const [moduleFilters, setModuleFilters] = useState({ 'F&R': false, 'S&A': false, 'P&P': false });

    const toggleModuleFilter = (mod) => {
        setModuleFilters(prev => ({ ...prev, [mod]: !prev[mod] }));
    };

    const activeModuleList = Object.keys(moduleFilters).filter(m => moduleFilters[m]);

    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [allMasterFields, setAllMasterFields] = useState({}); 
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInterface, setEditingInterface] = useState(null);
    const [unschedulingInterface, setUnschedulingInterface] = useState(null);
    const [showClearProjectModal, setShowClearProjectModal] = useState(false);
    const [isClearingProject, setIsClearingProject] = useState(false);
    const [pastingToDropId, setPastingToDropId] = useState(null);
    const [removingDropId, setRemovingDropId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [allUsersForSearch, setAllUsersForSearch] = useState([]);

    const [interfaceSearchTerm, setInterfaceSearchTerm] = useState('');
    const [dateClipboard, setDateClipboard] = useState(null);

    const [toastInfo, setToastInfo] = useState({ message: '', id: 0 });
    const [delayedSortState, setDelayedSortState] = useState(activeInterfaces);

    const categoryContentRef = useRef(null);

    // Pre-populate manager into the User Management section so they're visible from the start
    useEffect(() => {
        if (user && user.role === 'manager') {
            setAssignedUsers(prev => {
                if (prev.some(u => u.id === user.id)) return prev;
                return [...prev, { id: user.id, username: user.username }];
            });
        }
    }, [user]);

    // --- AUTO-SAVE USEEFFECT ---
    useEffect(() => {
        const draftState = {
            projectName,
            activeInterfaces,
            drops,
            dropLimit,
            assignedUsers,
            selectedFields,
            activeCategory,
            activeDropId,
            expandedInterfaces,
            importedCustomFieldIds,
            importedCustomInterfaceIds
        };
        sessionStorage.setItem('wysupp_create_project_draft', JSON.stringify(draftState));
    }, [projectName, activeInterfaces, drops, dropLimit, assignedUsers, selectedFields, activeCategory, activeDropId, expandedInterfaces, importedCustomFieldIds, importedCustomInterfaceIds]);

    useEffect(() => {
        if (toastInfo.message) {
            const timer = setTimeout(() => setToastInfo({ message: '', id: 0 }), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastInfo]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (
                categoryContentRef.current &&
                document.activeElement &&
                categoryContentRef.current.contains(document.activeElement) &&
                !document.activeElement.classList.contains('interface-search-input')
            ) {
                document.activeElement.blur();
            }
            setDelayedSortState(activeInterfaces);
        }, 500); 
        return () => clearTimeout(timer);
    }, [activeInterfaces]);


    const groupedMasterFields = useMemo(() => {
        const grouped = {};
        Object.entries(allMasterFields).forEach(([interfaceName, fields]) => {
            if (!Array.isArray(fields)) {
                grouped[interfaceName] = {};
                return;
            }
            grouped[interfaceName] = fields.reduce((acc, field) => {
                const fileName = field.source_file || 'Standard Fields';
                if (!acc[fileName]) {
                    acc[fileName] = [];
                }
                acc[fileName].push(field);
                return acc;
            }, {});
        });
        return grouped;
    }, [allMasterFields]);

    const activeDrop = useMemo(() => drops.find(d => d.id === activeDropId), [drops, activeDropId]);

    const groupedTasks = useMemo(() => {
        if (!activeDrop) return {};
        return activeDrop.tasks.reduce((acc, task) => {
            const key = task.interface_name;
            if (!acc[key]) acc[key] = [];
            acc[key].push(task);
            return acc;
        }, {});
    }, [activeDrop]);


    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Unclaimed custom interfaces/fields are hidden from the API by default so an
                // abandoned import can't leak into everyone's form. Ask for the ones this
                // draft created back, so a page refresh mid-import keeps them visible.
                const pendingInterfaceIds = (draft?.importedCustomInterfaceIds || []).join(',');
                const pendingFieldIds = (draft?.importedCustomFieldIds || []).join(',');

                const [fieldsRes, usersRes, interfacesRes] = await Promise.all([
                    axiosInstance.get('/api/master-interface-fields', { params: { pending_ids: pendingFieldIds || undefined } }),
                    axiosInstance.get('/api/users'),
                    axiosInstance.get('/api/master-interfaces/', { params: { pending_ids: pendingInterfaceIds || undefined } })
                ]);
                setAllMasterFields(fieldsRes.data.interfaces || {});
                setAllUsersForSearch(usersRes.data || []);

                const fetchedInterfaces = interfacesRes.data || [];
                const grouped = {};
                const flatList = [];

                fetchedInterfaces.forEach(iface => {
                    const meta = INTERFACE_METADATA[iface.interface_name];
                    const category = meta ? meta.category : "Drop 5 - Additional Data";
                    
                    const processed = {
                        key: iface.interface_name,
                        label: meta ? meta.label : iface.interface_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                        modules: meta ? meta.modules : []
                    };
                    
                    if (!grouped[category]) grouped[category] = [];
                    grouped[category].push(processed);
                    flatList.push(processed);
                });

                const sortedGrouped = {};
                DROP_NAMES.forEach(dropName => {
                    if (grouped[dropName]) {
                        sortedGrouped[dropName] = grouped[dropName];
                    }
                });
                
                Object.keys(grouped).forEach(key => {
                    if (!sortedGrouped[key]) sortedGrouped[key] = grouped[key];
                });

                setCategorizedInterfaces(sortedGrouped);
                setFlatAllInterfaces(flatList);
                
                if (!draft?.activeCategory) {
                    setActiveCategory(Object.keys(sortedGrouped)[0] || "Drop 1 - Master Data");
                }

                setLoading(false); 
            } catch (err) {
                if (err.response?.status === 401) return;
                setError(err.response?.data?.detail || 'Could not fetch initial data.');
                setLoading(false); 
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!activeDropId && drops.length > 0) {
            setActiveDropId(drops[0].id);
        }
        if (activeDropId && !drops.some(d => d.id === activeDropId)) {
            setActiveDropId(drops.length > 0 ? drops[0].id : null);
        }
    }, [drops, activeDropId]);

    useEffect(() => {
        if (userSearchTerm.length < 2) {
            setUserSearchResults([]);
            return;
        }
        const fetchUsers = async () => {
            setIsSearching(true);
            try {
                const response = await axiosInstance.get(`/api/v1/users/autocomplete?q=${userSearchTerm}`);
                const assignedIds = new Set(assignedUsers.map(u => u.id));
                setUserSearchResults(response.data.filter(u => !assignedIds.has(u.id)));
                setIsSearching(false); 
            } catch (err) {
                if (err.response?.status === 401) return;
                console.error("Failed to search users:", err);
                setIsSearching(false); 
            }
        };
        const debounce = setTimeout(() => { fetchUsers(); }, 300);
        return () => clearTimeout(debounce);
    }, [userSearchTerm, assignedUsers]);

    // --- CLEAR PROJECT HANDLER WITH CLEANUP ---
    const confirmClearProject = async () => {
        setIsClearingProject(true);

        // Trigger database garbage collection!
        if (importedCustomFieldIds.length > 0 || importedCustomInterfaceIds.length > 0) {
            try {
                await axiosInstance.post('/api/master-interface-fields/cleanup', {
                    field_ids: importedCustomFieldIds,
                    interface_ids: importedCustomInterfaceIds // <-- Wipe interfaces too
                });
            } catch (e) {
                console.error("Failed to clean up orphaned data:", e);
            }
        }

        // Destroy the auto-save draft
        sessionStorage.removeItem('wysupp_create_project_draft');

        // Force a full page reload to wipe React's memory and fetch a clean database slate
        window.location.reload();
    };

    // --- EXCEL IMPORT ---
    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsParsingExcel(true);
        setError('');
        
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axiosInstance.post('/api/projects/parse-interface-document', formData);
            const { importedActiveInterfaces, importedInterfaceDrops, importedSelectedFields, newlyCreatedFields, newlyCreatedInterfaces } = response.data;

            if (newlyCreatedInterfaces && newlyCreatedInterfaces.length > 0) {
                const newIfaceIds = newlyCreatedInterfaces.map(i => i.id);
                setImportedCustomInterfaceIds(prev => [...prev, ...newIfaceIds]);

                setFlatAllInterfaces(prev => [
                    ...prev, 
                    ...newlyCreatedInterfaces.map(i => ({ key: i.key, label: i.label, modules: ["Custom"] }))
                ]);

                setCategorizedInterfaces(prev => {
                    const next = { ...prev };
                    // Note: Ensure this string perfectly matches your Drop 5 name in DROP_NAMES
                    const drop5Name = "Drop 5 - Additional Data"; 
                    if (!next[drop5Name]) next[drop5Name] = [];
                    
                    newlyCreatedInterfaces.forEach(i => {
                        next[drop5Name].push({ key: i.key, label: i.label, modules: ["Custom"] });
                    });
                    return next;
                });
            }

            if (newlyCreatedFields && newlyCreatedFields.length > 0) {
                const newIds = newlyCreatedFields.map(f => f.id);
                setImportedCustomFieldIds(prev => [...prev, ...newIds]); 
                
                setAllMasterFields(prev => {
                    const updated = { ...prev };
                    newlyCreatedFields.forEach(field => {
                        if (!updated[field.interface_name]) {
                            updated[field.interface_name] = [];
                        }
                        if (!updated[field.interface_name].find(f => f.id === field.id)) {
                            updated[field.interface_name].push(field);
                        }
                    });
                    return updated;
                });

                // --- NEW: Check if the newly created fields are "Perfect" ---
                let incompleteCount = 0;
                newlyCreatedFields.forEach(field => {
                    const isPerfect = 
                        field.file_header_name?.trim() &&
                        field.data_type?.trim() &&
                        field.table_in_relex?.trim() &&
                        field.ui_field_in_relex?.trim() &&
                        field.used_for?.trim() &&
                        field.description?.trim() &&
                        field.example_value?.trim();
                        
                    if (!isPerfect) {
                        incompleteCount++;
                    }
                });

                if (incompleteCount > 0) {
                    setIncompleteFieldsWarning(incompleteCount);
                }
            }

            // Check for newly created custom interfaces that ended up with no fields
            if (newlyCreatedInterfaces && newlyCreatedInterfaces.length > 0) {
                const emptyCount = newlyCreatedInterfaces.filter(
                    iface => !importedSelectedFields[iface.key] || importedSelectedFields[iface.key].length === 0
                ).length;
                if (emptyCount > 0) {
                    setEmptyInterfacesWarning(emptyCount);
                }
            }

            // A document may schedule past the manual drop limit; raise the limit to its highest drop.
            const docDropNumbers = Object.values(importedInterfaceDrops || {});
            if (docDropNumbers.length > 0) {
                setDropLimit(prev => Math.max(prev, ...docDropNumbers));
            }

            // Set Active Interfaces AND Auto-Assign to Drops
            setActiveInterfaces(prev => {
                const nextActive = { ...prev, ...importedActiveInterfaces };
                const newlyActivatedKeys = Object.keys(importedActiveInterfaces).filter(k => !prev[k]);

                if (newlyActivatedKeys.length > 0) {
                    setDrops(prevDrops => {
                        let updatedDrops = [...prevDrops];
                        
                        newlyActivatedKeys.forEach(key => {
                            // The document's "Interface summary" tab decides the drop. Interfaces it
                            // doesn't number fall back to the drop of their default category.
                            const docDropNumber = importedInterfaceDrops?.[key];
                            let targetCategory = docDropNumber ? getStandardDropName(docDropNumber) : null;
                            if (!targetCategory) {
                                for (const [categoryName, interfacesArray] of Object.entries(categorizedInterfaces)) {
                                    if (interfacesArray.some(int => int.key === key)) {
                                        targetCategory = categoryName;
                                        break;
                                    }
                                }
                            }

                            if (targetCategory) {
                                let targetDropIndex = updatedDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));

                                const newTasks = DEFAULT_TASKS.map(taskName => ({
                                    id: `new-${key}-${taskName}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, 
                                    interface_name: key,
                                    task_name: taskName,
                                    start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
                                }));

                                if (targetDropIndex >= 0) {
                                    const existingDrop = updatedDrops[targetDropIndex];
                                    if (!existingDrop.tasks.some(t => t.interface_name === key)) {
                                        updatedDrops[targetDropIndex] = {
                                            ...existingDrop,
                                            tasks: [...existingDrop.tasks, ...newTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
                                        };
                                    }
                                } else {
                                    const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                                    const dropNumber = docDropNumber || (dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : updatedDrops.length + 1);

                                    updatedDrops.push({
                                        id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                                        name: targetCategory,
                                        drop_number: dropNumber,
                                        tasks: newTasks
                                    });
                                    updatedDrops.sort((a, b) => a.drop_number - b.drop_number);
                                }
                            }
                        });
                        return updatedDrops;
                    });

                    // Auto-expand the imported interfaces so they are visible in the schedule
                    setExpandedInterfaces(prevExp => {
                        const nextExp = { ...prevExp };
                        newlyActivatedKeys.forEach(k => nextExp[k] = true);
                        return nextExp;
                    });
                }
                return nextActive;
            });

            setSelectedFields(prev => ({ ...prev, ...importedSelectedFields }));
            setToastInfo({ message: `Document parsed! Imported active interfaces and ${newlyCreatedFields?.length || 0} custom fields.`, id: Date.now() });

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to parse the Interface Document. Please ensure it follows the RELEX standard format.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsParsingExcel(false);
            if (excelInputRef.current) {
                excelInputRef.current.value = null; 
            }
        }
    };

    // --- State Callbacks for Field Edits/Deletes ---
    const handleFieldUpdated = (updatedField, interfaceName) => {
        setAllMasterFields(prev => ({
            ...prev,
            [interfaceName]: prev[interfaceName].map(f => f.id === updatedField.id ? updatedField : f)
        }));
        setToastInfo({ message: 'Custom field updated successfully!', id: Date.now() });
    };

    const handleFieldDeleted = (fieldId, interfaceName) => {
        setAllMasterFields(prev => ({
            ...prev,
            [interfaceName]: prev[interfaceName].filter(f => f.id !== fieldId)
        }));
        setSelectedFields(prev => ({
            ...prev,
            [interfaceName]: (prev[interfaceName] || []).filter(id => id !== fieldId)
        }));
        setImportedCustomFieldIds(prev => prev.filter(id => id !== fieldId));
        setToastInfo({ message: 'Custom field permanently deleted.', id: Date.now() });
    };

    const toggleInterfaceExpansion = (interfaceName) => {
        setExpandedInterfaces(prev => ({
            ...prev,
            [interfaceName]: !prev[interfaceName]
        }));
    };

    const handleAddUser = (user) => {
        setAssignedUsers(prev => [...prev, user]);
        setUserSearchTerm('');
        setUserSearchResults([]);
    };

    const handleRemoveUser = (userId) => {
        setAssignedUsers(prev => prev.filter(user => user.id !== userId));
    };

    const handleCopyDates = (dropId, sourceInterfaceName) => {
        const drop = drops.find(d => d.id === dropId);
        if (!drop) return;

        const sourceTasks = drop.tasks.filter(t => t.interface_name === sourceInterfaceName);
        const dateMap = sourceTasks.reduce((acc, task) => {
            acc[task.task_name] = {
                start_date: task.start_date,
                end_date: task.end_date,
                responsible_user_ids: task.responsible_user_ids
            };
            return acc;
        }, {});

        setDateClipboard(dateMap);
        const ifaceLabel = flatAllInterfaces.find(i => i.key === sourceInterfaceName)?.label || sourceInterfaceName;
        setToastInfo({ message: `Copied dates & users from ${ifaceLabel}`, id: Date.now() });
    };

    const handlePasteDates = (dropId, targetInterfaceName) => {
        if (!dateClipboard) {
            setToastInfo({ message: 'Clipboard is empty. Copy dates & users first.', id: Date.now() });
            return;
        }

        setDrops(prevDrops => {
            return prevDrops.map(drop => {
                if (drop.id !== dropId) return drop;

                const newTasks = drop.tasks.map(task => {
                    if (task.interface_name === targetInterfaceName) {
                        const copiedData = dateClipboard[task.task_name];
                        if (copiedData) {
                            return {
                                ...task,
                                start_date: copiedData.start_date,
                                end_date: copiedData.end_date,
                                responsible_user_ids: copiedData.responsible_user_ids
                            };
                        }
                    }
                    return task;
                });
                return { ...drop, tasks: newTasks };
            });
        });

        const ifaceLabel = flatAllInterfaces.find(i => i.key === targetInterfaceName)?.label || targetInterfaceName;
        setToastInfo({ message: `Pasted dates & users to ${ifaceLabel}`, id: Date.now() });
    };

    const handlePasteDatesToDrop = (dropId) => {
        if (!dateClipboard) {
            setToastInfo({ message: 'Clipboard is empty. Copy dates & users first.', id: Date.now() });
            return;
        }

        const drop = drops.find(d => d.id === dropId);
        if (!drop || drop.tasks.length === 0) return;

        setPastingToDropId(dropId);
    };

    const confirmPasteDatesToDrop = () => {
        const dropId = pastingToDropId;
        setPastingToDropId(null);

        const drop = drops.find(d => d.id === dropId);
        if (!drop || !dateClipboard) return;

        setDrops(prevDrops => {
            return prevDrops.map(d => {
                if (d.id !== dropId) return d;

                const newTasks = d.tasks.map(task => {
                    const copiedData = dateClipboard[task.task_name];
                    if (copiedData) {
                        return {
                            ...task,
                            start_date: copiedData.start_date,
                            end_date: copiedData.end_date,
                            responsible_user_ids: copiedData.responsible_user_ids
                        };
                    }
                    return task;
                });
                return { ...d, tasks: newTasks };
            });
        });

        setToastInfo({ message: `Pasted dates & users to all interfaces in ${drop.name}`, id: Date.now() });
    };

    const handleUnscheduleInterface = (dropId, interfaceName) => {
        setUnschedulingInterface({ dropId, interfaceName });
    };

    const confirmUnscheduleInterface = () => {
        if (!unschedulingInterface) return;
        const { dropId, interfaceName } = unschedulingInterface;
        const ifaceLabel = flatAllInterfaces.find(i => i.key === interfaceName)?.label || interfaceName;

        setDrops(prevDrops => {
            return prevDrops.map(drop => {
                if (drop.id !== dropId) return drop;
                const newTasks = drop.tasks.filter(task => task.interface_name !== interfaceName);
                return { ...drop, tasks: newTasks };
            });
        });

        setExpandedInterfaces(prev => {
            const newState = { ...prev };
            delete newState[interfaceName];
            return newState;
        });

        setUnschedulingInterface(null);
        setToastInfo({ message: `Interface "${ifaceLabel}" moved to "Available to Schedule".`, id: Date.now() });
    };


    const unassignedInterfaces = useMemo(() => {
        const interfacesInDrops = new Set(drops.flatMap(d => d.tasks.map(t => t.interface_name)));
        return flatAllInterfaces.filter(int => activeInterfaces[int.key] && !interfacesInDrops.has(int.key));
    }, [activeInterfaces, drops, flatAllInterfaces]);

    const handleInterfaceCheckboxChange = (key) => {
        const isNowActive = !activeInterfaces[key];
        setActiveInterfaces(prev => ({ ...prev, [key]: isNowActive }));

        if (isNowActive) {
            const ifaceLabel = flatAllInterfaces.find(i => i.key === key)?.label || key;
            setToastInfo({ message: `"${ifaceLabel}" added to project!`, id: Date.now() });

            let targetCategory = null;
            for (const [categoryName, interfacesArray] of Object.entries(categorizedInterfaces)) {
                if (interfacesArray.some(int => int.key === key)) {
                    targetCategory = categoryName;
                    break;
                }
            }

            if (targetCategory) {
                setDrops(prevDrops => {
                    let updatedDrops = [...prevDrops];
                    let targetDropIndex = updatedDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));

                    const newTasks = DEFAULT_TASKS.map(taskName => ({
                        id: `new-${key}-${taskName}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, 
                        interface_name: key,
                        task_name: taskName,
                        start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
                    }));

                    if (targetDropIndex >= 0) {
                        const existingDrop = updatedDrops[targetDropIndex];
                        updatedDrops[targetDropIndex] = {
                            ...existingDrop,
                            tasks: [...existingDrop.tasks, ...newTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
                        };
                    } else {
                        const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                        const dropNumber = dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : updatedDrops.length + 1;

                        updatedDrops.push({
                            id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            name: targetCategory,
                            drop_number: dropNumber,
                            tasks: newTasks
                        });

                        updatedDrops.sort((a, b) => a.drop_number - b.drop_number);
                    }
                    return updatedDrops;
                });

                setExpandedInterfaces(prev => ({ ...prev, [key]: true }));
            }

            if (allMasterFields[key] && !selectedFields[key]) {
                const mandatoryFieldIds = allMasterFields[key]
                    .filter(field => field.is_mandatory)
                    .map(field => field.id);
                setSelectedFields(prev => ({ ...prev, [key]: mandatoryFieldIds }));
            }
        } else {
            setDrops(prevDrops => prevDrops.map(drop => ({
                ...drop,
                tasks: drop.tasks.filter(t => t.interface_name !== key)
            })).filter(drop => drop.tasks.length > 0 || drop.id.startsWith('new-drop'))
            );
            setSelectedFields(prev => {
                const newSelected = { ...prev };
                delete newSelected[key];
                return newSelected;
            });
        }
    };

    const handleFieldSelectionChange = (interfaceName, newSelectedIds) => {
        setSelectedFields(prev => ({ ...prev, [interfaceName]: newSelectedIds }));
    };

    const handleOpenModal = (interfaceName) => {
        setEditingInterface(interfaceName);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingInterface(null);
    };

    const handleAddDrop = () => {
        if (drops.length < dropLimit) {
            // Take the lowest free drop number so re-adding a removed drop never repeats an existing one.
            const usedNumbers = new Set(drops.map(getDropNumber));
            let dropNumber = 1;
            while (usedNumbers.has(dropNumber)) dropNumber++;

            const newDropId = `new-drop-${Date.now()}`;
            const newDropName = getStandardDropName(dropNumber);
            setDrops([...drops, { id: newDropId, name: newDropName, drop_number: dropNumber, tasks: [] }].sort((a, b) => getDropNumber(a) - getDropNumber(b)));
            setActiveDropId(newDropId);
        }
    };

    const handleDropNameChange = (dropId, newLabel) => {
        setDrops(prevDrops => prevDrops.map(drop =>
            drop.id === dropId
                ? { ...drop, name: buildDropName(getDropPrefix(drop.name), newLabel) }
                : drop
        ));
    };

    const confirmRemoveDrop = () => {
        const dropId = removingDropId;
        setRemovingDropId(null);
        setDrops(prev => prev.filter(d => d.id !== dropId));
        if (activeDropId === dropId) {
            setActiveDropId(null);
        }
    };

    const handleDragStart = (e, interfaceKey) => {
        e.dataTransfer.setData("interfaceKey", interfaceKey);
    };

    const handleDrop = (e, targetDropId) => {
        e.preventDefault();
        if (!targetDropId) return;
        const interfaceKey = e.dataTransfer.getData("interfaceKey");
        if (!interfaceKey) return;

        setExpandedInterfaces(prev => ({ ...prev, [interfaceKey]: true }));

        let movedTasks = [];
        const newDrops = drops.map(drop => {
            const tasksToKeep = drop.tasks.filter(task => {
                if (task.interface_name === interfaceKey) {
                    movedTasks.push(task);
                    return false;
                }
                return true;
            });
            return { ...drop, tasks: tasksToKeep };
        });

        if (movedTasks.length === 0) {
            movedTasks = DEFAULT_TASKS.map(taskName => ({
                id: `new-${interfaceKey}-${taskName}-${Date.now()}`,
                interface_name: interfaceKey,
                task_name: taskName,
                start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
            }));
        }

        const finalDrops = newDrops.map(drop =>
            drop.id === targetDropId
                ? { ...drop, tasks: [...drop.tasks, ...movedTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name)) }
                : drop
        );
        setDrops(finalDrops);
    };


    const handleDragOver = (e) => e.preventDefault();

    const handleTaskChange = (dropId, taskId, field, value) => {
        setDrops(prevDrops => prevDrops.map(drop => {
            if (drop.id !== dropId) return drop;
            return {
                ...drop,
                tasks: drop.tasks.map(task => {
                    if (task.id !== taskId) return task;
                    if (field === 'responsible_user_ids') {
                        return { ...task, responsible_user_ids: value };
                    }
                    return { ...task, [field]: value };
                })
            };
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        if (!projectName.trim()) {
            setError("Project name is required.");
            setSaving(false);
            return;
        }

        if (unassignedInterfaces.length > 0) {
            setError(`Cannot create project. Please assign all ${unassignedInterfaces.length} active interface(s) to a drop before saving.`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSaving(false);
            return;
        }

        const fieldsPayload = [];
        const allSelectedFieldIds = new Set();
        Object.keys(selectedFields).forEach(ifaceKey => {
            if (activeInterfaces[ifaceKey]) {
                selectedFields[ifaceKey].forEach(id => allSelectedFieldIds.add(id));
            }
        });

        allSelectedFieldIds.forEach(fieldId => {
            fieldsPayload.push({
                field_id: fieldId,
                is_selected: true,
                reason_for_omission: null,
                is_mandatory_override: null
            });
        });

        const schedulePayload = drops.map((drop, index) => ({
            drop_name: drop.name,
            interfaces: Array.from(new Set(drop.tasks.map(task => task.interface_name)))
        }));

        // Includes the tracking IDs for final database allocation
        const projectData = {
            project_name: projectName.trim(),
            ...activeInterfaces,
            selected_field_ids: fieldsPayload,
            schedule: schedulePayload,
            imported_custom_field_ids: importedCustomFieldIds,
            imported_custom_interface_ids: importedCustomInterfaceIds
        };

        try {
            // 1. Create the Project 
            const response = await axiosInstance.post('/api/projects/', projectData);
            const newProjectId = response.data.id;

            // 2. Update Users
            const userIdsToSave = assignedUsers.map(u => u.id);

            const userUpdatePayload = { user_ids: userIdsToSave };
            await axiosInstance.put(`/api/projects/${newProjectId}/users`, userUpdatePayload);

            // 3. Update Schedule
            const scheduleDetailsPayload = {
                drops: drops.map((drop, index) => ({
                    drop_number: index + 1,
                    name: drop.name,
                    tasks: drop.tasks.map(task => ({
                        interface_name: task.interface_name,
                        task_name: task.task_name,
                        start_date: task.start_date || null,
                        end_date: task.end_date || null,
                        status: task.status,
                        responsible_user_ids: task.responsible_user_ids || []
                    }))
                }))
            };
            await axiosInstance.put(`/api/projects/${newProjectId}/schedule`, scheduleDetailsPayload);

            // SUCCESS! Clean up the draft!
            sessionStorage.removeItem('wysupp_create_project_draft');
            setProjectName('');
            setActiveInterfaces({});
            setSelectedFields({});
            setDrops([]);
            setDropLimit(MAX_DROPS);
            setAssignedUsers([]);
            setActiveCategory(Object.keys(categorizedInterfaces)[0] || "Drop 1 - Master Data");
            setActiveDropId(null);
            setExpandedInterfaces({});
            setDateClipboard(null);
            setImportedCustomFieldIds([]);
            setImportedCustomInterfaceIds([]);
            
            setSuccess(`Project "${response.data.project_name}" created successfully!`);

            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSaving(false); 
        } catch (err) {
            if (err.response?.status === 401) return;

            let errorMessage = "An unexpected error occurred. Please try again.";
            const errorDetail = err.response?.data?.detail;

            if (typeof errorDetail === 'string') {
                if (errorDetail.includes("already exists")) {
                    errorMessage = "A project with this name already exists. Please choose a different name.";
                } else {
                    errorMessage = errorDetail;
                }
            } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
                const firstError = errorDetail[0];
                errorMessage = `Validation Error: ${firstError.msg} (in ${firstError.loc.join(' > ')})`;
            } else {
                errorMessage = err.message;
            }

            setError(errorMessage);
            setSaving(false); 
        }
    };

    return (
        <div className="admin-page-container">
            {toastInfo.message && (
                <div key={toastInfo.id} className="toast-notification">
                    <FaCheckCircle size={18} />
                    {toastInfo.message}
                </div>
            )}
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/admin/manage-projects')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="page-title">Create New Project</h1>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading initial data...</p>
                    </div>
                ) : (
                    <>
                        <div className="form-wrapper">
                            {error && <div className="form-feedback error">{error}</div>}
                            {success && <div className="form-feedback success">{success}</div>}

                            <form onSubmit={handleSubmit} className="project-form">
                                {/* Section 1: Project Details */}
                                <div className="form-section">
                                    <div className="form-section-header-row">
                                        <h2 className="form-section-title no-border">1. Project Details</h2>
                                        <button type="button" onClick={() => setShowClearProjectModal(true)} className="clear-project-btn" title="Clear all entered data">
                                            <FaTrash /> Clear Form
                                        </button>
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="project-name" className="input-label">Project Name</label>
                                        <input id="project-name" type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
                                    </div>
                                </div>

                                {/* Section 2: Active Interfaces & Fields */}
                                <div className="form-section">
                                    <div className="form-section-header-row">
                                        <h2 className="form-section-title no-border">2. Active Interfaces & Fields</h2>
                                        
                                        <div style={{ display: 'flex', gap: '1rem' }}>
                                            <input 
                                                type="file" 
                                                id="import-excel" 
                                                accept=".xlsx" 
                                                style={{ display: 'none' }} 
                                                onChange={handleImportExcel} 
                                                ref={excelInputRef}
                                            />
                                            <button 
                                                type="button" 
                                                onClick={() => document.getElementById('import-excel').click()} 
                                                className="action-button secondary"
                                                disabled={isParsingExcel}
                                                title="Upload a customized RELEX Interface Document to auto-select interfaces and fields"
                                            >
                                                {isParsingExcel ? <FaSpinner className="button-spinner" style={{ marginRight: '8px' }} /> : <FaFileExcel style={{ marginRight: '8px' }} />}
                                                {isParsingExcel ? 'Parsing Document...' : 'Import from Interfaces Document'}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="category-layout">
                                        <div className="category-sidebar">
                                            {Object.keys(categorizedInterfaces).map(category => {
                                                const interfacesInDrop = categorizedInterfaces[category] || [];
                                                const totalInDrop = interfacesInDrop.length;
                                                const selectedInDrop = interfacesInDrop.filter(int => activeInterfaces[int.key]).length;
                                                return (
                                                    <button key={category} type="button" className={`category-button ${activeCategory === category ? 'active' : ''}`} onClick={() => { setActiveCategory(category); setInterfaceSearchTerm(''); }}>
                                                        <span className="category-button-label">{category}</span>
                                                        <span className="category-button-counter">({selectedInDrop} / {totalInDrop})</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="category-content" ref={categoryContentRef}>
                                            {categorizedInterfaces[activeCategory] && (
                                                <>
                                                    <div className="interface-search-container">
                                                        <input
                                                            type="text"
                                                            placeholder={`Search ${activeCategory} interfaces...`}
                                                            value={interfaceSearchTerm}
                                                            onChange={(e) => setInterfaceSearchTerm(e.target.value)}
                                                            className="interface-search-input"
                                                        />
                                                        <div className="module-filters">
                                                            <span style={{ fontSize: '0.85rem', color: 'var(--light-text)', marginRight: '0.5rem' }}>Filter by Module:</span>
                                                            {['F&R', 'S&A', 'P&P'].map(mod => (
                                                                <button
                                                                    key={mod}
                                                                    type="button"
                                                                    className={`module-filter-btn ${moduleFilters[mod] ? 'active' : ''}`}
                                                                    onClick={() => toggleModuleFilter(mod)}
                                                                >
                                                                    {mod}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="checkbox-grid">
                                                        {categorizedInterfaces[activeCategory]
                                                            .filter(int => {
                                                                if (!int.label.toLowerCase().includes(interfaceSearchTerm.toLowerCase())) return false;
                                                                if (activeModuleList.length > 0) {
                                                                    if (!int.modules || int.modules.length === 0) return false;
                                                                    return int.modules.some(mod => activeModuleList.includes(mod));
                                                                }
                                                                return true;
                                                            })
                                                            .sort((a, b) => (delayedSortState[b.key] || false) - (delayedSortState[a.key] || false))
                                                            .map(int => {
                                                                const currentSelectedIds = selectedFields[int.key] || [];
                                                                const totalFields = allMasterFields[int.key]?.length || 0;
                                                                
                                                                return (
                                                                    <div key={int.key} className={`checkbox-item-wrapper ${activeInterfaces[int.key] ? 'is-selected' : ''}`}>
                                                                        <Checkbox id={`create-${int.key}`} name={int.key} checked={activeInterfaces[int.key] || false} onChange={() => handleInterfaceCheckboxChange(int.key)} label={int.label} />
                                                                        
                                                                        <div className="interface-module-tags">
                                                                            {int.modules && int.modules.map(mod => (
                                                                                <span key={mod} className={`module-tag tag-${mod.replace('&', '')}`}>{mod}</span>
                                                                            ))}
                                                                        </div>

                                                                        <div className={`interface-actions-compact ${activeInterfaces[int.key] ? 'visible' : 'hidden'}`}>
                                                                            {allMasterFields[int.key] && (
                                                                                <button type="button" onClick={() => handleOpenModal(int.key)} className="field-selector-button">
                                                                                    Fields ({currentSelectedIds.length} / {totalFields})
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                    {categorizedInterfaces[activeCategory]
                                                        .filter(int => {
                                                            if (!int.label.toLowerCase().includes(interfaceSearchTerm.toLowerCase())) return false;
                                                            if (activeModuleList.length > 0) {
                                                                if (!int.modules || int.modules.length === 0) return false;
                                                                return int.modules.some(mod => activeModuleList.includes(mod));
                                                            }
                                                            return true;
                                                        }).length === 0 && (
                                                        <div className="no-users-message" style={{ marginTop: '1.5rem' }}>
                                                            No interfaces match your search and module filters.
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 3: User Management */}
                                <div className="form-section">
                                    <h2 className="form-section-title">3. User Management</h2>
                                    <div className="user-management-grid">
                                        <div className="assigned-users-list">
                                            <h4>Assigned Users</h4>
                                            {assignedUsers.length > 0 ? (
                                                <ul>
                                                    {assignedUsers.map(user => (
                                                        <li key={user.id}>
                                                            <span>{user.username}</span>
                                                            <button type="button" onClick={() => handleRemoveUser(user.id)}>&times;</button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="no-users-message">No users assigned yet.</p>
                                            )}
                                        </div>
                                        <div className="add-user-form">
                                            <h4>Add User to Project</h4>
                                            <div className="autocomplete-wrapper">
                                                <input type="text" placeholder="Search by username or email..." value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)} />
                                                {userSearchResults.length > 0 && (
                                                    <div className="autocomplete-results">
                                                        {userSearchResults.map(user => (
                                                            <div key={user.id} className="autocomplete-item" onClick={() => handleAddUser(user)}>
                                                                <strong>{user.username}</strong> <span>({user.email})</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {isSearching && <div className="autocomplete-results"><div className="autocomplete-item">Searching...</div></div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section 4: Schedule */}
                                <div className="form-section">
                                    <div className="schedule-section-header">
                                        <h2 className="form-section-title">4. Schedule Organization & Dates</h2>
                                    </div>
                                    <div className="schedule-editor">
                                        <div className="unassigned-pool">
                                            <h4>Available to Schedule</h4>
                                            {unassignedInterfaces.length > 0 && (
                                                <p className="unassigned-warning-message">
                                                    All interfaces must be assigned to a drop before saving.
                                                </p>
                                            )}
                                            <div className="interface-list" onDrop={(e) => handleDrop(e, activeDropId)} onDragOver={handleDragOver}>
                                                {unassignedInterfaces.map(int => (
                                                    <div key={int.key} draggable onDragStart={(e) => handleDragStart(e, int.key)} className="draggable-item">{int.label}</div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="schedule-content-wrapper">
                                            <div className="drop-tabs-container">
                                                {drops.map(drop => (
                                                    <button key={drop.id} type="button" title={drop.name} className={`drop-tab ${activeDropId === drop.id ? 'active' : ''}`} onClick={() => setActiveDropId(drop.id)}>
                                                        {drop.name.split(' - ')[0]}
                                                    </button>
                                                ))}
                                                {drops.length < dropLimit && <button type="button" onClick={handleAddDrop} className="add-drop-tab-button">+ Add Drop</button>}
                                            </div>

                                            <div className="active-drop-area" onDrop={(e) => handleDrop(e, activeDropId)} onDragOver={handleDragOver}>
                                                {!activeDrop ? (
                                                    <p className="drop-placeholder">Please add or select a drop to begin scheduling.</p>
                                                ) : (
                                                    <>
                                                        <div className="drop-header">
                                                            <div className="drop-name-editor">
                                                                {getDropPrefix(activeDrop.name) && (
                                                                    <span className="drop-name-prefix">{getDropPrefix(activeDrop.name)} -</span>
                                                                )}
                                                                <input
                                                                    type="text"
                                                                    value={getDropLabel(activeDrop.name)}
                                                                    onChange={(e) => handleDropNameChange(activeDrop.id, e.target.value)}
                                                                    className="drop-name-input"
                                                                    placeholder="Name this drop"
                                                                    title="Rename this drop"
                                                                    aria-label="Drop name"
                                                                    maxLength={80}
                                                                />
                                                            </div>
                                                            <div className="drop-header-actions">
                                                                <button
                                                                    type="button"
                                                                    className="copy-dates-button paste"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePasteDatesToDrop(activeDrop.id);
                                                                    }}
                                                                    title={`Paste copied dates & users to all interfaces in ${activeDrop.name}`}
                                                                    disabled={!dateClipboard || activeDrop.tasks.length === 0}
                                                                >
                                                                    <FaPaste /> Paste to Entire Drop
                                                                </button>
                                                                <button type="button" onClick={() => setRemovingDropId(activeDrop.id)} className="remove-drop-button">&times;</button>
                                                            </div>
                                                        </div>
                                                        <div className="task-table-container">
                                                            {Object.keys(groupedTasks).length > 0 ? (
                                                                <table className="task-table">
                                                                    <thead><tr><th>Task</th><th>Start</th><th>End</th><th>Responsible</th></tr></thead>
                                                                    <tbody>
                                                                        {Object.entries(groupedTasks).map(([interfaceName, tasks]) => (
                                                                            <React.Fragment key={interfaceName}>
                                                                                <tr className="interface-group-header">
                                                                                    <td colSpan="4">
                                                                                        <div className="interface-header-cell">
                                                                                            <div className="interface-header-label" onClick={() => toggleInterfaceExpansion(interfaceName)}>
                                                                                                <span>{flatAllInterfaces.find(i => i.key === interfaceName)?.label}</span>
                                                                                                <span className="toggle-icon">
                                                                                                    {expandedInterfaces[interfaceName] ? <FaChevronUp /> : <FaChevronDown />}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="interface-header-actions">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="copy-dates-button"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleCopyDates(activeDrop.id, interfaceName);
                                                                                                    }}
                                                                                                    title={`Copy dates & users from ${flatAllInterfaces.find(i => i.key === interfaceName)?.label}`}
                                                                                                >
                                                                                                    <FaCopy /> Copy Dates & Users
                                                                                                </button>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="copy-dates-button paste"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handlePasteDates(activeDrop.id, interfaceName);
                                                                                                    }}
                                                                                                    title={`Paste copied dates & users to ${flatAllInterfaces.find(i => i.key === interfaceName)?.label}`}
                                                                                                    disabled={!dateClipboard}
                                                                                                >
                                                                                                    <FaPaste /> Paste Dates & Users
                                                                                                </button>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="unschedule-button"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleUnscheduleInterface(activeDrop.id, interfaceName);
                                                                                                    }}
                                                                                                    title={`Remove "${flatAllInterfaces.find(i => i.key === interfaceName)?.label}" from schedule`}
                                                                                                >
                                                                                                    <FaTimes />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                                {expandedInterfaces[interfaceName] && tasks.map(task => (
                                                                                    <tr key={task.id} className="task-row">
                                                                                        <td className="task-name-cell">{task.task_name}</td>
                                                                                        <td><input type="date" value={task.start_date || ''} onChange={e => handleTaskChange(activeDrop.id, task.id, 'start_date', e.target.value)} /></td>
                                                                                        <td><input type="date" value={task.end_date || ''} onChange={e => handleTaskChange(activeDrop.id, task.id, 'end_date', e.target.value)} /></td>
                                                                                        <td><MultiUserSelect allUsers={assignedUsers} selectedUserIds={task.responsible_user_ids || []} onChange={(newSelectedIds) => handleTaskChange(activeDrop.id, task.id, 'responsible_user_ids', newSelectedIds)} /></td>
                                                                                    </tr>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : <p className="drop-placeholder">Drop interfaces from the "Available to Schedule" list here.</p>}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="form-actions">
                                    <button type="button" className="action-button secondary" onClick={() => navigate('/admin/manage-projects')}>Cancel</button>
                                    <button
                                        type="submit"
                                        disabled={saving || unassignedInterfaces.length > 0}
                                        className="action-button primary"
                                        title={unassignedInterfaces.length > 0 ? `Cannot create: ${unassignedInterfaces.length} interface(s) are still in 'Available to Schedule'` : 'Create project'}
                                    >
                                        {saving ? 'Creating...' : 'Create Project'}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* FieldSelectorModal */}
                        {isModalOpen && editingInterface && (
                            <FieldSelectorModal
                                isOpen={isModalOpen}
                                onClose={handleCloseModal}
                                interfaceName={editingInterface}
                                fields={groupedMasterFields[editingInterface] || {}}
                                selectedFieldIds={selectedFields[editingInterface] || []}
                                onSelectionChange={handleFieldSelectionChange}
                                onFieldUpdated={handleFieldUpdated}
                                onFieldDeleted={handleFieldDeleted}
                            />
                        )}

                        {/* --- Post-Import Warning Modal --- */}
                        {(incompleteFieldsWarning > 0 || emptyInterfacesWarning > 0) && (
                            <div className="modal-overlay" style={{ zIndex: 9999 }}>
                                <div className="modal-content" style={{ maxWidth: '450px', textAlign: 'center', padding: '2rem' }}>
                                    <center>
                                        <FaExclamationTriangle style={{ fontSize: '3rem', color: '#fcd34d', marginBottom: '1rem' }} />
                                    </center>
                                    <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Review Required After Import</h3>
                                    <div style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem', textAlign: 'left' }}>
                                        {incompleteFieldsWarning > 0 && (
                                            <p style={{ marginBottom: emptyInterfacesWarning > 0 ? '1rem' : 0 }}>
                                                <strong>{incompleteFieldsWarning}</strong> custom field(s) were created but are missing some documentation (Table in RELEX, Description, Example Value, etc.).
                                            </p>
                                        )}
                                        {emptyInterfacesWarning > 0 && (
                                            <p style={{ marginBottom: 0 }}>
                                                <strong>{emptyInterfacesWarning}</strong> custom interface(s) were created but have <strong>no fields associated</strong> with them — their sheet may not have matched the standard format, or all fields were excluded.
                                            </p>
                                        )}
                                        <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                                            Once you finish creating this project, please navigate to the <strong>Edit Project</strong> page to review and complete these items.
                                        </p>
                                    </div>
                                    <button
                                        className="action-button primary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => { setIncompleteFieldsWarning(0); setEmptyInterfacesWarning(0); }}
                                    >
                                        Understood
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* --- Unschedule Interface Confirmation Modal --- */}
                        {unschedulingInterface && (
                            <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setUnschedulingInterface(null)}>
                                <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '2rem' }} onClick={e => e.stopPropagation()}>
                                    <center>
                                        <FaExclamationTriangle style={{ fontSize: '3rem', color: '#fcd34d', marginBottom: '1rem' }} />
                                    </center>
                                    <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Remove From This Drop?</h3>
                                    <p style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                        <strong style={{ color: 'var(--text-color)' }}>{flatAllInterfaces.find(i => i.key === unschedulingInterface.interfaceName)?.label || unschedulingInterface.interfaceName}</strong> will be moved back to the <strong>"Available to Schedule"</strong> list.
                                        <br /><br />
                                        The dates and responsible users set for it in this drop will be cleared.
                                    </p>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <button
                                            type="button"
                                            className="action-button secondary"
                                            style={{ flex: 1, justifyContent: 'center' }}
                                            onClick={() => setUnschedulingInterface(null)}
                                        >
                                            Keep Scheduled
                                        </button>
                                        <button
                                            type="button"
                                            className="action-button danger"
                                            style={{ flex: 1, justifyContent: 'center' }}
                                            onClick={confirmUnscheduleInterface}
                                        >
                                            Remove From Drop
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <ConfirmModal
                            isOpen={showClearProjectModal}
                            tone="danger"
                            title="Clear This Form?"
                            message={<>
                                All data entered on this form will be discarded and the page will reload.
                                {(importedCustomFieldIds.length > 0 || importedCustomInterfaceIds.length > 0) && (
                                    <><br /><br /><strong style={{ color: '#fca5a5' }}>Any interfaces and fields imported via the Interface Document will also be permanently deleted.</strong></>
                                )}
                                <br /><br />
                                This cannot be undone.
                            </>}
                            confirmLabel={isClearingProject ? 'Clearing...' : 'Clear Form'}
                            cancelLabel="Keep Editing"
                            isBusy={isClearingProject}
                            onConfirm={confirmClearProject}
                            onClose={() => setShowClearProjectModal(false)}
                        />

                        <ConfirmModal
                            isOpen={pastingToDropId !== null}
                            tone="warning"
                            confirmVariant="primary"
                            title="Paste To Entire Drop?"
                            message={<>
                                The copied dates and responsible users will be applied to all <strong style={{ color: 'var(--text-color)' }}>{new Set((drops.find(d => d.id === pastingToDropId)?.tasks || []).map(t => t.interface_name)).size}</strong> interfaces in this drop.
                                <br /><br />
                                Any dates already set on those interfaces will be overwritten.
                            </>}
                            confirmLabel="Paste to All"
                            cancelLabel="Cancel"
                            onConfirm={confirmPasteDatesToDrop}
                            onClose={() => setPastingToDropId(null)}
                        />

                        <ConfirmModal
                            isOpen={removingDropId !== null}
                            tone="danger"
                            title="Remove This Drop?"
                            message={<>
                                <strong style={{ color: 'var(--text-color)' }}>{drops.find(d => d.id === removingDropId)?.name}</strong> and all of its scheduled interfaces will be removed from this project.
                                <br /><br />
                                Those interfaces stay selected and will return to the "Available to Schedule" list.
                            </>}
                            confirmLabel="Remove Drop"
                            cancelLabel="Keep Drop"
                            onConfirm={confirmRemoveDrop}
                            onClose={() => setRemovingDropId(null)}
                        />
                    </>
                )}
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --optional-override-color: #fcd34d;
                    --optional-override-bg: rgba(245, 158, 11, 0.1);
                }
                /* Base styles */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .dashboard-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 3rem; background-color: var(--card-bg); border-bottom: 1px solid var(--border-color); }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); }
                .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
                .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
                .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .back-button:hover { background: #4a5568; }
                .page-title { font-size: 2rem; font-weight: 700; color: var(--text-color); }

                /* Form structure */
                .form-wrapper { background-color: var(--card-bg); border-radius: 12px; padding: 2rem; border: 1px solid var(--border-color); }
                .project-form { display: flex; flex-direction: column; gap: 2.5rem; }
                .form-section { border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; background-color: #1a202c; }
                
                .form-section-header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid var(--border-color);
                }
                .form-section-title { font-size: 1.25rem; font-weight: 600; margin: 0 0 1.5rem 0; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .form-section-title.no-border {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .clear-project-btn {
                    background-color: transparent;
                    border: 1px solid var(--danger-color);
                    color: var(--danger-color);
                    padding: 0.4rem 0.8rem;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }
                .clear-project-btn:hover {
                    background-color: var(--error-bg);
                }

                /* Input fields */
                .input-group { display: flex; flex-direction: column; }
                .input-label { font-size: 0.9rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
                input:not([type="checkbox"]), select, textarea { width: 100%; padding: 0.75rem 1rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; font-size: 1rem; color: var(--text-color); transition: border-color 0.2s; box-sizing: border-box; }
                input:not([type="checkbox"]):focus, select:focus, textarea:focus { outline: none; border-color: var(--green-accent); }

                /* Interfaces section */
                .category-layout { display: flex; gap: 1.5rem; border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--input-bg); }
                .category-sidebar { flex: 0 0 250px; padding: 1rem; border-right: 1px solid var(--border-color); }
                .category-button {
                    display: flex; justify-content: space-between; align-items: center; width: 100%;
                    padding: 0.75rem 1rem; margin-bottom: 0.5rem; border: none; border-radius: 6px;
                    background-color: transparent; cursor: pointer; text-align: left; font-size: 0.95rem;
                    font-weight: 500; color: var(--light-text); transition: background-color 0.2s, color 0.2s;
                }
                .category-button:hover { background-color: var(--card-bg); }
                .category-button.active { background-color: var(--green-accent); color: var(--dark-bg); font-weight: 600; }
                .category-button-label { flex-grow: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 0.5rem; }
                .category-button-counter { font-size: 0.8rem; font-weight: 400; color: var(--light-text); flex-shrink: 0; }
                .category-button.active .category-button-counter { color: var(--dark-bg); opacity: 0.8; font-weight: 500; }

                .category-content { 
                    flex-grow: 1; 
                    padding: 1.5rem; 
                    max-height: 400px; 
                    overflow-y: auto; 
                    overflow-anchor: none;
                }
                .checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; }

                .interface-search-container { 
                    margin-bottom: 1.5rem; 
                    position: sticky; 
                    top: -1.5rem; 
                    z-index: 10; 
                    background-color: var(--input-bg); 
                    padding: 1rem 0 0.5rem 0; 
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .interface-search-input { 
                    width: 100%; 
                    padding: 0.85rem 1.2rem; 
                    background-color: var(--card-bg); 
                    border: 1px solid var(--border-color); 
                    border-radius: 8px; 
                    color: var(--text-color); 
                    font-size: 0.95rem; 
                    transition: all 0.2s ease; 
                }
                .interface-search-input:focus { 
                    outline: none; 
                    border-color: var(--green-accent); 
                    box-shadow: 0 0 0 1px var(--green-accent); 
                }
                .module-filters {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .module-filter-btn {
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    color: var(--text-color);
                    padding: 0.25rem 0.6rem;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .module-filter-btn:hover {
                    background-color: #4a5568;
                }
                .module-filter-btn.active {
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                    border-color: var(--green-accent);
                }

                .checkbox-item-wrapper { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; display: flex; flex-direction: column; justify-content: space-between; min-height: 100px; }
                .checkbox-container { flex-grow: 1; }
                .checkbox-wrapper { display: flex; align-items: center; }
                .checkbox-label { font-weight: 500; color: var(--text-color); }
                .checkbox-label.disabled { color: var(--light-text); cursor: not-allowed; }
                
                .interface-module-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.4rem;
                    margin-top: 0.5rem;
                    margin-left: 1.8rem;
                }
                .module-tag {
                    font-size: 0.65rem;
                    font-weight: 700;
                    padding: 0.15rem 0.4rem;
                    border-radius: 4px;
                    text-transform: uppercase;
                }
                .tag-FR { background-color: var(--success-bg); color: var(--green-accent); border: 1px solid rgba(72, 187, 120, 0.3); }
                .tag-SA { background-color: var(--optional-override-bg); color: var(--optional-override-color); border: 1px solid rgba(245, 158, 11, 0.3); }
                .tag-PP { background-color: var(--blue-bg); color: rgba(59, 131, 246, 0.69); border: 1px solid rgba(59, 130, 246, 0.3); }

                .checkbox-input {
                    -webkit-appearance: none; appearance: none; flex-shrink: 0;
                    width: 18px; height: 18px; border: 2px solid var(--border-color); border-radius: 4px;
                    background-color: var(--input-bg); display: flex; align-items: center; justify-content: center;
                    margin-right: 0.75rem; cursor: pointer; transition: border-color 0.2s;
                }
                .checkbox-input:hover:not(:disabled) { border-color: var(--light-text); }
                .checkbox-input:checked { border-color: var(--green-accent); }
                .checkbox-input::before {
                    content: ""; width: 10px; height: 10px; transform: scale(0);
                    transition: transform 0.1s ease-in-out; box-shadow: inset 1em 1em var(--green-accent); border-radius: 2px;
                }
                .checkbox-input:checked::before { transform: scale(1); }
                .checkbox-input:disabled { opacity: 0.6; cursor: not-allowed; border-color: var(--border-color); }
                .checkbox-input:disabled:checked { border-color: var(--green-accent); opacity: 0.6; }
                .checkbox-input:disabled::before { box-shadow: inset 1em 1em var(--green-accent); }


                .interface-actions-compact { margin-top: 1rem; transition: opacity 0.2s ease-in-out; }
                .interface-actions-compact.hidden { opacity: 0; pointer-events: none; }
                .interface-actions-compact.visible { opacity: 1; pointer-events: auto; }
                .field-selector-button { width: 100%; padding: 0.6rem 0.75rem; background-color: var(--border-color); border: none; color: var(--text-color); border-radius: 6px; cursor: pointer; font-size: 0.85rem; font-weight: 500; transition: all 0.2s; }
                .field-selector-button:hover { background-color: #5a6578; }

                /* User Management Section */
                .user-management-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; align-items: start; }
                h4 { font-size: 1rem; font-weight: 600; color: var(--light-text); margin: 0 0 1rem 0; }
                .assigned-users-list ul { list-style: none; padding: 0; margin: 0; max-height: 250px; overflow-y: auto; }
                .assigned-users-list li { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; margin-bottom: 0.5rem; }
                .assigned-users-list li button { background: none; border: none; color: #ef4444; font-size: 1.2rem; cursor: pointer; }
                .no-users-message { color: var(--light-text); font-style: italic; padding: 1rem; text-align: center; border-radius: 6px; border: 1px dashed var(--border-color); min-height: 52.5px; display:flex; align-items:center; justify-content:center; }
                .autocomplete-wrapper { position: relative; }
                .autocomplete-results { position: absolute; top: 100%; left: 0; right: 0; background-color: var(--card-bg); border: 1px solid var(--border-color); border-top: none; border-radius: 0 0 6px 6px; z-index: 100; max-height: 200px; overflow-y: auto; }
                .autocomplete-item { padding: 0.75rem 1rem; cursor: pointer; }
                .autocomplete-item:hover { background-color: #4a5568; }
                .autocomplete-item strong { color: var(--text-color); }
                .autocomplete-item span { color: var(--light-text); font-size: 0.9em; margin-left: 0.5rem; }

                /* Schedule Section */
                .schedule-section-header {
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;
                }
                .schedule-section-header .form-section-title {
                    margin-bottom: 0; padding-bottom: 0; border-bottom: none;
                }
                .clipboard-status-message {
                    font-size: 0.9rem; color: var(--green-accent); background-color: var(--success-bg); padding: 0.5rem 1rem; border-radius: 6px; animation: fadeInOut 3s ease-in-out;
                }
                @keyframes fadeInOut {
                    0%, 100% { opacity: 0; }
                    10%, 90% { opacity: 1; }
                }

                .toast-notification {
                    position: fixed;
                    top: 2rem;
                    right: 2rem;
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                    padding: 1rem 1.5rem;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 600;
                    z-index: 9999;
                    pointer-events: none;
                    animation: slideInToast 0.3s cubic-bezier(0.16, 1, 0.3, 1), fadeOutToast 0.3s ease-in 2.7s forwards;
                }

                @keyframes slideInToast {
                    from { transform: translateX(120%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                @keyframes fadeOutToast {
                    to { opacity: 0; visibility: hidden; }
                }

                .checkbox-item-wrapper {
                    transition: border-color 0.3s ease, box-shadow 0.3s ease;
                }
                
                .checkbox-item-wrapper.is-selected {
                    border-color: var(--green-accent);
                    box-shadow: 0 4px 12px rgba(72, 187, 120, 0.15);
                    animation: selectionPulse 0.4s ease-out;
                }

                @keyframes selectionPulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.03); }
                    100% { transform: scale(1); }
                }
                
                .schedule-editor { display: grid; grid-template-columns: 280px 1fr; gap: 1.5rem; }
                .unassigned-pool { background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; }
                .interface-list { min-height: 200px; padding: 0.5rem; border-radius: 6px; border: 2px dashed var(--border-color); }
                .draggable-item { padding: 0.6rem 0.8rem; background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 6px; margin-bottom: 0.5rem; cursor: grab; font-size: 0.9rem; }
                .draggable-item:active { cursor: grabbing; background-color: #4a5568; }

                .unassigned-warning-message {
                    font-size: 0.85rem; color: var(--optional-override-color); background-color: var(--optional-override-bg); border: 1px solid var(--optional-override-color); padding: 0.75rem; border-radius: 6px; margin-bottom: 1rem; text-align: center; font-weight: 500;
                }

                .schedule-content-wrapper { display: flex; flex-direction: column; gap: 1rem; }
                .drop-tabs-container { display: flex; flex-wrap: wrap; gap: 0.5rem; border-bottom: 1px solid var(--border-color); padding-bottom: 1rem; }
                .drop-tab, .add-drop-tab-button { padding: 0.6rem 1.2rem; border: 1px solid var(--border-color); border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
                .drop-tab { background-color: var(--input-bg); color: var(--light-text); }
                .drop-tab:hover { background-color: var(--card-bg); }
                .drop-tab.active { background-color: var(--green-accent); color: var(--dark-bg); border-color: var(--green-accent); }
                .add-drop-tab-button { background-color: transparent; color: var(--green-accent); border-style: dashed; }
                .add-drop-tab-button:hover { background-color: rgba(72, 187, 120, 0.1); border-color: var(--green-accent); }

                .active-drop-area { padding: 1rem; border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--input-bg); min-height: 300px; }
                .drop-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
                .drop-header-actions { display: flex; align-items: center; gap: 1rem; }
                .drop-name-editor { display: flex; align-items: center; gap: 0.35rem; flex: 1; min-width: 0; margin-right: 1vw; }
                .drop-name-prefix { font-weight: 600; font-size: 1.1rem; color: var(--text-color); white-space: nowrap; }
                .drop-name-input { font-weight: 600; border: 1px solid transparent; border-radius: 4px; background-color: transparent; flex: 1; min-width: 0; padding: 0.25rem; font-size: 1.1rem; color: var(--text-color); transition: border-color 0.2s, background-color 0.2s; }
                .drop-name-input:hover { border-color: var(--border-color); }
                .drop-name-input:focus { outline: none; border-color: var(--green-accent); background-color: var(--input-bg); }
                .remove-drop-button { background: none; border: none; font-size: 1.5rem; color: var(--light-text); cursor: pointer; }
                .remove-drop-button:hover { color: #ef4444; }
                .drop-placeholder { text-align: center; color: var(--light-text); padding: 2rem; }

                .task-table-container { overflow-x: auto; }
                .task-table { width: 100%; border-collapse: collapse; }
                .task-table th, .task-table td { padding: 0.75rem; text-align: left; font-size: 0.9rem; vertical-align: middle; }
                .task-table thead { background-color: #374151; }
                .task-table th { font-weight: 600; color: var(--light-text); }

                .interface-group-header td {
                    background-color: #374151; font-weight: 600; color: var(--text-color); padding: 0; overflow: hidden;
                }
                .interface-header-cell {
                    display: flex; justify-content: space-between; align-items: center; width: 100%;
                    padding: 0.75rem 1rem; box-sizing: border-box;
                }
                .interface-header-label {
                    display: flex; align-items: center; gap: 0.5rem; cursor: pointer; flex-grow: 1; flex-shrink: 1;
                    min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .toggle-icon { color: var(--light-text); transition: transform 0.2s ease-in-out; flex-shrink: 0; }
                
                .interface-header-actions {
                    display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; margin-left: 1rem;
                }

                .copy-dates-button {
                    background-color: var(--border-color); color: var(--text-color); border: none; border-radius: 4px;
                    padding: 0.3rem 0.6rem; font-size: 0.75rem; font-weight: 500; cursor: pointer;
                    transition: background-color 0.2s; flex-shrink: 0; white-space: nowrap;
                    display: inline-flex; align-items: center; gap: 0.3rem;
                }
                .copy-dates-button:hover { background-color: #5a6578; }
                .copy-dates-button.paste {
                    margin-left: 0;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                }
                .copy-dates-button.paste:hover:not(:disabled) {
                    background-color: var(--border-color);
                }
                .copy-dates-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .unschedule-button {
                    background-color: transparent; color: var(--light-text); border: 1px solid var(--border-color); border-radius: 4px; padding: 0.3rem; font-size: 0.75rem; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; line-height: 1;
                }
                .unschedule-button:hover {
                    background-color: var(--error-bg); color: var(--danger-color); border-color: var(--danger-color);
                }

                .task-row { border-left: 2px solid var(--border-color); }
                .task-row td { border-bottom: 1px solid var(--border-color); }
                .task-row:last-child td { border-bottom: none; }
                .task-name-cell { padding-left: 1.5rem; color: var(--light-text); }

                .task-table input[type="date"] {
                    background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px;
                    color: var(--text-color); padding: 0.6rem; color-scheme: dark;
                }
                .task-table input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.6) brightness(1.2); cursor: pointer; }

                /* Multi User Select styles */
                .multi-select-container { position: relative; min-width: 150px; }
                .selected-users-pills { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; border: 1px solid var(--border-color); border-radius: 6px; padding: 4px 0.75rem; min-height: 42px; cursor: pointer; background-color: var(--input-bg); transition: border-color 0.2s; }
                .selected-users-pills:hover { border-color: var(--light-text); }
                .user-pill { display: flex; justify-content: space-between; align-items: center; background-color: var(--border-color); border-radius: 4px; padding: 0.25rem 0.5rem; font-size: 0.8rem; }
                .user-pill button { background: none; border: none; margin-left: 0.5rem; font-size: 1rem; cursor: pointer; color: var(--light-text); }
                .no-users-text { color: var(--light-text); font-style: normal; font-size: 0.9rem; }
                .user-dropdown-list { position: absolute; background: var(--card-bg); border: 1px solid var(--border-color); border-radius: 6px; z-index: 10; width: 100%; max-height: 150px; overflow-y: auto; margin-top: 0.25rem; }
                .user-dropdown-item { padding: 0.5rem; cursor: pointer; font-size: 0.9rem; }
                .user-dropdown-item:hover { background-color: #4a5568; }
                .user-dropdown-item.disabled { color: var(--light-text); cursor: not-allowed; }

                /* Form actions */
                .form-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem; }
                .action-button { padding: 0.75rem 1.5rem; border: none; border-radius: 6px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .action-button.primary { background-color: var(--green-accent); color: var(--dark-bg); }
                .action-button.primary:hover:not(:disabled) { background-color: #38a169; }
                .action-button.secondary { background-color: var(--border-color); color: var(--text-color); }
                .action-button.secondary:hover { background-color: #5a6578; }
                .action-button.danger { background-color: var(--danger-color); color: var(--text-color); }
                .action-button.danger:hover:not(:disabled) { background-color: #dc2626; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }

                /* Feedback messages */
                .form-feedback { text-align: center; padding: 0.75rem; border-radius: 6px; margin-bottom: 1.5rem; }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }

                /* Modal base styles */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; padding: 1rem; }
                .modal-content { background: var(--card-bg); border: 1px solid var(--border-color); padding: 0; border-radius: 8px; width: 90%; max-width: 500px; max-height: 90vh; display: flex; flex-direction: column; }
                .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid var(--border-color); }
                .modal-header h3 { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .modal-close-button { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: var(--light-text); }
                .modal-body { overflow-y: auto; padding: 0; }
                .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1rem 1.5rem; border-top: 1px solid var(--border-color); }
                .modal-content.large-modal { max-width: 800px; }

                /* Scrollbars */
                .category-content::-webkit-scrollbar, .modal-body::-webkit-scrollbar, .user-dropdown-list::-webkit-scrollbar { width: 14px; }
                .category-content::-webkit-scrollbar-track, .modal-body::-webkit-scrollbar-track, .user-dropdown-list::-webkit-scrollbar-track { background: var(--input-bg); }
                .category-content::-webkit-scrollbar-thumb, .modal-body::-webkit-scrollbar-thumb, .user-dropdown-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 10px; border: 3px solid var(--input-bg); }
                .category-content::-webkit-scrollbar-thumb:hover, .modal-body::-webkit-scrollbar-thumb:hover, .user-dropdown-list::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                /* Field Selector Modal specifics */
                .dropdown-actions { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--border-color); background-color: var(--dark-bg); position: sticky; top: 0; z-index: 1; }
                .dropdown-actions span { font-size: 0.85rem; color: var(--light-text); font-weight: 500; }
                .dropdown-actions button { background: none; border: none; color: var(--green-accent); font-weight: 600; cursor: pointer; padding: 0.25rem 0.5rem; }
                .field-modal-disclaimer {
                    font-size: 0.85rem; color: var(--light-text); background-color: var(--dark-bg); padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--border-color); text-align: center; font-style: italic;
                }
                .field-groups-container { display: flex; flex-direction: column; }
                .file-group-header {
                    font-size: 0.9rem; font-weight: 700; color: var(--green-accent); background-color: var(--dark-bg); padding: 0.5rem 1.5rem; border-bottom: 1px solid var(--border-color); border-top: 1px solid var(--border-color); margin: 0; text-transform: uppercase; letter-spacing: 0.05em;
                }
                .file-group:first-child .file-group-header { border-top: none; }
                .field-list { list-style: none; padding: 0.5rem; margin: 0; }

                .field-item { display: flex; align-items: flex-start; padding: 0.75rem 1rem; border-radius: 4px; transition: background-color 0.2s; }
                .field-item:hover { background-color: var(--dark-bg); }
                .field-item input[type="checkbox"] { margin-right: 1rem; margin-top: 2px; }
                .field-item input[type="checkbox"] {
                    -webkit-appearance: none; appearance: none;
                    flex-shrink: 0; width: 18px; height: 18px; border: 2px solid var(--border-color); border-radius: 4px;
                    background-color: var(--input-bg); display: flex; align-items: center; justify-content: center;
                    cursor: pointer; transition: border-color 0.2s;
                }
                .field-item input[type="checkbox"]:hover:not(:disabled) { border-color: var(--light-text); }
                .field-item input[type="checkbox"]:checked { border-color: var(--green-accent); }
                .field-item input[type="checkbox"]::before {
                    content: ""; width: 10px; height: 10px; transform: scale(0);
                    transition: transform 0.1s ease-in-out; box-shadow: inset 1em 1em var(--green-accent); border-radius: 2px;
                }
                .field-item input[type="checkbox"]:checked::before { transform: scale(1); }
                .field-item input[type="checkbox"]:disabled { opacity: 0.6; cursor: not-allowed; border-color: var(--border-color); }
                .field-item input[type="checkbox"]:disabled:checked { border-color: var(--green-accent); opacity: 0.6; }
                .field-item input[type="checkbox"]:disabled::before { box-shadow: inset 1em 1em var(--green-accent); }

                .field-item-content { flex-grow: 1; display: flex; flex-direction: column; }
                .field-item label.field-item-label-container { 
                    cursor: pointer; display: flex; align-items: center; justify-content: space-between; width: 100%; color: var(--text-color); margin-bottom: 0.25rem; 
                }
                .field-item input[type="checkbox"]:disabled + .field-item-content label.field-item-label-container { cursor: not-allowed; color: var(--light-text); }

                .field-label-text { flex-grow: 1; }
                .field-tags-and-actions { display: flex; align-items: center; gap: 0.75rem; }
                .mandatory-tag { font-size: 0.7rem; font-weight: 700; padding: 0.1rem 0.4rem; border-radius: 9px; }
                .mandatory-tag { color: var(--error-color); background-color: var(--error-bg); }
                
                /* NEW: Custom Field Styles */
                .custom-tag { background-color: rgba(167, 139, 250, 0.15); color: #a78bfa; border: 1px solid rgba(167, 139, 250, 0.3); font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.4rem; border-radius: 4px; text-transform: uppercase; }
                .custom-field-actions { display: flex; gap: 0.4rem; margin-left: 0.5rem; }
                .edit-btn, .delete-btn { background: none; border: none; cursor: pointer; padding: 0.2rem; border-radius: 4px; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; }
                .edit-btn { color: var(--light-text); }
                .edit-btn:hover { color: var(--blue-accent, #60a5fa); background-color: rgba(96, 165, 250, 0.1); }
                .delete-btn { color: var(--light-text); }
                .delete-btn:hover { color: var(--danger-color, #ef4444); background-color: rgba(239, 68, 68, 0.1); }
                .custom-field-form { background-color: var(--dark-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; margin-bottom: 1.5rem; }
                .form-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
                .form-header-row h4 { font-size: 1.1rem; color: var(--text-color); margin: 0; }
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
                .input-group.full-width { grid-column: 1 / -1; }

                /* Loading state */
                .loading-state { text-align: center; color: var(--text-color); padding-top: 5rem; }
                .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                .button-spinner { animation: spin 1s linear infinite; }
                
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default CreateProject;